import { DateTime } from "luxon";
import { EEmitterType, EFrequencyType, EIntervalType, EMode, EOnEmitActions, ESchedulerMode, EStatus, EWeekdays } from "../enums/event.enums";
import { IEvent, TFrequency, IExclusionRule, TBufferedTimes } from "../interfaces/event.interfaces";

export function buildEventPayload(params: {
  mode: EMode;
  schedulerMode?: ESchedulerMode;
  at?: string;
  startDate?: string;
  endDate?: string;
  duration?: number;
  frequencyType?: EFrequencyType | "eachNMinutes" | "eachNHours" | "eachNDays";
  intervalValue?: number;
  each?: number;
  weekdays?: EWeekdays[];
  monthDays?: string[];
  yearDates?: string[];
  exclusions?: IExclusionRule[];
  wallTime?: { timeZone: string; ambiguous: "earlier" | "later"; nonexistent: "skip" | "shiftForward" };
  typeName?: string;
  fixedTimes?: string[];
  bufferedTimes?: any;
}): IEvent {
  const {
    mode,
    schedulerMode = ESchedulerMode.FIXED,
    startDate,
    endDate,
    duration,
    at,
    frequencyType = EFrequencyType.DAILY,
    intervalValue = 1,
    weekdays = [],
    monthDays = [],
    yearDates = [],
    exclusions = [],
    wallTime,
    each = 1,
    fixedTimes,
    bufferedTimes,
  } = params;

  const eventBase = {
    name: "jobs-schedule",
    typeName: process.env.REACT_APP_JOB_SCHEDULER ?? "flownect-job-scheduler",
    emitter: {
      type: EEmitterType.WEBHOOK,
      url: process.env.REACT_APP_WEBHOOK_EVENT ?? "http://flownect-connectacleaner-back:3001/v1/jobs/notification",
      onEmit: EOnEmitActions.WAIT_FOR_RESPONSE,
    },
    status: EStatus.PENDING,
    mode,
    duration,
  };

  const frequencyMap: Record<string, () => TFrequency> = {
    [EFrequencyType.DAILY]: () => ({
      type: EFrequencyType.DAILY,
      at,
      exclusionRules: exclusions,
    }),
    [EFrequencyType.WEEKLY]: () => ({
      type: EFrequencyType.WEEKLY,
      interval: weekdays.map((w) => ({
        type: EIntervalType.WEEKDAY,
        value: w,
      })),
      at,
      wallTime,
      each,
      exclusionRules: exclusions,
    }),
    [EFrequencyType.MONTHLY]: () => ({
      type: EFrequencyType.MONTHLY,
      interval: monthDays.map((d) => ({
        type: EIntervalType.DATE_DAY,
        value: d,
      })),
      at,
      wallTime,
      each,
      exclusionRules: exclusions,
    }),
    [EFrequencyType.YEARLY]: () => ({
      type: EFrequencyType.YEARLY,
      interval: yearDates.map((d) => ({
        type: EIntervalType.DATE,
        value: d,
      })),
      at,
      wallTime,
      exclusionRules: exclusions,
    }),
    hourly: () => ({
      type: "hourly",
      exclusionRules: exclusions,
    }),
    eachNMinutes: () => ({
      type: "eachNMinutes",
      interval: { type: EIntervalType.UNIT, value: intervalValue },
      exclusionRules: exclusions,
    }),
    eachNHours: () => ({
      type: "eachNHours",
      interval: { type: EIntervalType.UNIT, value: intervalValue },
      exclusionRules: exclusions,
    }),
    eachNDays: () => ({
      type: "eachNDays",
      interval: { type: EIntervalType.UNIT, value: intervalValue },
      exclusionRules: exclusions,
    }),
  };

  const buildFrequency = frequencyMap[frequencyType] ?? frequencyMap[EFrequencyType.DAILY];
  const frequency = buildFrequency();

  const eventConfig = mode === EMode.SINGLE_EXECUTION ? { ...eventBase, startDate } : { ...eventBase, startDate, endDate, frequency };

  let scheduleTimes: any = {};

  if (mode === EMode.SINGLE_EXECUTION && schedulerMode === ESchedulerMode.BUFFERED && fixedTimes && fixedTimes.length > 0) {
    scheduleTimes.bufferedTimes = convertIsoToBufferedFormat(fixedTimes);
  } else if (fixedTimes) {
    scheduleTimes.fixedTimes = fixedTimes;
  } else if (bufferedTimes) {
    scheduleTimes.bufferedTimes = bufferedTimes;
  }

  return {
    eventPreferences: {
      eventConfig,
      ...scheduleTimes,
    },
  } as unknown as IEvent;
}

function convertIsoToBufferedFormat(
  isoStrings: string[]
): Array<{ start: string; end: string; date: string }> {
  const result: Array<{ start: string; end: string; date: string }> = [];

  for (let i = 0; i < isoStrings.length; i += 2) {
    const startIso = isoStrings[i];
    const endIso = isoStrings[i + 1];

    if (!startIso || !endIso) break;

    const startDt = DateTime.fromISO(startIso);
    const endDt = DateTime.fromISO(endIso);

    if (!startDt.isValid || !endDt.isValid) continue;

    result.push({
      start: startDt.toFormat("HH:mm"),
      end: endDt.toFormat("HH:mm"),
      date: startDt.toFormat("yyyy-MM-dd"),
    });
  }

  return result;
}
